import { z } from 'zod/v3';

export type OnboardingOption<TValue extends string = string> = {
  value: TValue;
  label: string;
  description?: string;
  hint?: string;
};

export const ONBOARDING_TEAM_SIZE_VALUES = [
  '1',
  '2-10',
  '11-50',
  '51-200',
  '200+',
] as const;

export const ONBOARDING_INDUSTRY_VALUES = [
  'B2B_SAAS',
  'FINTECH',
  'HEALTHCARE',
  'ECOMMERCE',
  'OTHER',
] as const;

export const ONBOARDING_WORK_MODEL_VALUES = [
  'REMOTE',
  'HYBRID',
  'OFFICE',
] as const;

// Recovery Time Objective — how long the business can be down.
// Buckets, not exact numbers; auditors care that a target is set, not the minute.
export const ONBOARDING_RTO_VALUES = [
  '<1h',
  '1-4h',
  '4-24h',
  '1-3d',
  '>3d',
] as const;

// Recovery Point Objective — acceptable data loss window.
export const ONBOARDING_RPO_VALUES = [
  '<15m',
  '15m-1h',
  '1-4h',
  '4-24h',
  '>24h',
] as const;

// Compliance-program maturity self-assessment (Issue #437 PR 2′).
export const ONBOARDING_MATURITY_VALUES = [
  'NOT_STARTED',
  'IN_PROGRESS',
  'NEAR_READY',
] as const;

// GRC platform the company is switching from, if any (Issue #437 PR 2′).
export const ONBOARDING_PREVIOUS_PLATFORM_VALUES = [
  'vanta',
  'drata',
  'secureframe',
  'thoropass',
  'sprinto',
  'none',
  'other',
] as const;

// HIPAA covered-entity vs business-associate scoping (Issue #437 PR 4).
export const ONBOARDING_HIPAA_ENTITY_TYPE_VALUES = [
  'COVERED_ENTITY',
  'BUSINESS_ASSOCIATE',
  'HYBRID',
] as const;

// Data types the product handles. MUST match the `ComplianceProgram.dataTypes`
// vocabulary consumed by the gap detectors (`data-scope-drift.detector.ts`
// `DECLARATION_LABEL`) and written by the web onboarding wizard — otherwise an
// agent-supplied value (e.g. "pii") is stored verbatim and the wizard treats
// the field as unset.
export const ONBOARDING_DATA_TYPE_VALUES = [
  'CUSTOMER_PII',
  'PAYMENT',
  'HEALTH',
  'EMPLOYEE',
  'IP',
  'CUSTOMER_IP',
] as const;

// SOC 2 audit type captured at onboarding (`ComplianceProgram.auditType`).
export const ONBOARDING_AUDIT_TYPE_VALUES = ['TYPE_I', 'TYPE_II'] as const;

// Program depth (`ComplianceProgram.policyComplexity`) — RFC-224. The stored
// vocabulary stays 'MINIMUM'/'BALANCED' so no value migration or reader sweep is
// needed, but the contract is program depth (active Tests + policy commitments +
// readiness together), not policy prose verbosity. Customer-facing labels are
// Baseline / Enterprise-ready; never surface the raw enum.
export const ONBOARDING_PROGRAM_DEPTH_VALUES = ['MINIMUM', 'BALANCED'] as const;

// Infrastructure hosting model (`ComplianceProgram.infrastructureModel`). MUST
// match the web wizard's radio values ('cloud'/'on-prem'/'hybrid'/'govcloud').
export const ONBOARDING_INFRASTRUCTURE_MODEL_VALUES = [
  'cloud',
  'on-prem',
  'hybrid',
  'govcloud',
] as const;

export type OnboardingTeamSizeValue =
  (typeof ONBOARDING_TEAM_SIZE_VALUES)[number];
export type OnboardingIndustryValue =
  (typeof ONBOARDING_INDUSTRY_VALUES)[number];
export type OnboardingWorkModelValue =
  (typeof ONBOARDING_WORK_MODEL_VALUES)[number];
export type OnboardingRtoValue = (typeof ONBOARDING_RTO_VALUES)[number];
export type OnboardingRpoValue = (typeof ONBOARDING_RPO_VALUES)[number];
export type OnboardingMaturityValue =
  (typeof ONBOARDING_MATURITY_VALUES)[number];
export type OnboardingPreviousPlatformValue =
  (typeof ONBOARDING_PREVIOUS_PLATFORM_VALUES)[number];
export type OnboardingHipaaEntityTypeValue =
  (typeof ONBOARDING_HIPAA_ENTITY_TYPE_VALUES)[number];

export const OnboardingTeamSizeSchema = z.enum(ONBOARDING_TEAM_SIZE_VALUES);
export const OnboardingIndustrySchema = z.enum(ONBOARDING_INDUSTRY_VALUES);
export const OnboardingWorkModelSchema = z.enum(ONBOARDING_WORK_MODEL_VALUES);
export const OnboardingRtoSchema = z.enum(ONBOARDING_RTO_VALUES);
export const OnboardingRpoSchema = z.enum(ONBOARDING_RPO_VALUES);
export const OnboardingMaturitySchema = z.enum(ONBOARDING_MATURITY_VALUES);
export const OnboardingPreviousPlatformSchema = z.enum(
  ONBOARDING_PREVIOUS_PLATFORM_VALUES,
);
export const OnboardingHipaaEntityTypeSchema = z.enum(
  ONBOARDING_HIPAA_ENTITY_TYPE_VALUES,
);

export type OnboardingDataTypeValue =
  (typeof ONBOARDING_DATA_TYPE_VALUES)[number];
export type OnboardingAuditTypeValue =
  (typeof ONBOARDING_AUDIT_TYPE_VALUES)[number];
export const OnboardingDataTypeSchema = z.enum(ONBOARDING_DATA_TYPE_VALUES);
export const OnboardingAuditTypeSchema = z.enum(ONBOARDING_AUDIT_TYPE_VALUES);

export type OnboardingProgramDepthValue =
  (typeof ONBOARDING_PROGRAM_DEPTH_VALUES)[number];
export const OnboardingProgramDepthSchema = z.enum(
  ONBOARDING_PROGRAM_DEPTH_VALUES,
);

export type OnboardingInfrastructureModelValue =
  (typeof ONBOARDING_INFRASTRUCTURE_MODEL_VALUES)[number];
export const OnboardingInfrastructureModelSchema = z.enum(
  ONBOARDING_INFRASTRUCTURE_MODEL_VALUES,
);

export const ONBOARDING_TEAM_SIZE_OPTIONS: ReadonlyArray<
  OnboardingOption<OnboardingTeamSizeValue>
> = [
  {
    value: '1',
    label: 'Just me',
    description: 'Solo founder',
  },
  {
    value: '2-10',
    label: '2-10',
    description: 'Early-stage — lean policies, broad role coverage',
  },
  {
    value: '11-50',
    label: '11-50',
    description: 'Scaling — first formal segregation of duties',
  },
  {
    value: '51-200',
    label: '51-200',
    description: 'Growth — dedicated owners per domain',
  },
  {
    value: '200+',
    label: '200+',
    description: 'Enterprise — mature program expected',
  },
] as const;

export const ONBOARDING_INDUSTRY_OPTIONS: ReadonlyArray<
  OnboardingOption<OnboardingIndustryValue>
> = [
  {
    value: 'B2B_SAAS',
    label: 'B2B SaaS',
    description: 'Software sold to businesses',
    hint: 'Software sold to businesses',
  },
  {
    value: 'FINTECH',
    label: 'Fintech',
    description: 'Financial technology or payments',
    hint: 'Financial technology or payments',
  },
  {
    value: 'HEALTHCARE',
    label: 'Healthcare',
    description: 'Health data or medical services',
    hint: 'Health data or medical services',
  },
  {
    value: 'ECOMMERCE',
    label: 'E-commerce',
    description: 'Retail or online marketplace',
    hint: 'Retail or online marketplace',
  },
  {
    value: 'OTHER',
    label: 'Other',
    description: 'Does not fit the above categories',
  },
] as const;

export const ONBOARDING_WORK_MODEL_OPTIONS: ReadonlyArray<
  OnboardingOption<OnboardingWorkModelValue>
> = [
  {
    value: 'REMOTE',
    label: 'Remote',
    description: 'Team works fully distributed',
    hint: 'Team works fully distributed',
  },
  {
    value: 'HYBRID',
    label: 'Hybrid',
    description: 'Mix of remote and in-office',
    hint: 'Mix of remote and in-office',
  },
  {
    value: 'OFFICE',
    label: 'Office',
    description: 'Primarily in a physical location',
    hint: 'Primarily in a physical location',
  },
] as const;

export const ONBOARDING_RTO_OPTIONS: ReadonlyArray<
  OnboardingOption<OnboardingRtoValue>
> = [
  {
    value: '<1h',
    label: 'Less than 1 hour',
    description: 'Mission-critical — needs hot standby or active-active failover',
    hint: 'Implies HA or multi-region — most early-stage SaaS does not commit to this',
  },
  {
    value: '1-4h',
    label: '4 hours',
    description: 'Tight — typical for revenue-impacting B2B SaaS',
    hint: 'Usually requires a managed HA database or warm standby',
  },
  {
    value: '4-24h',
    label: '24 hours',
    description: 'Reasonable for first-time audits — supportable with backups + IaC',
    hint: 'Default for early-stage SaaS; aligns with daily backup cadence',
  },
  {
    value: '1-3d',
    label: '3 days',
    description: 'Lenient — suitable for non-critical internal tooling',
  },
  {
    value: '>3d',
    label: 'More than 3 days',
    description: 'Best-effort recovery only',
  },
] as const;

export const ONBOARDING_RPO_OPTIONS: ReadonlyArray<
  OnboardingOption<OnboardingRpoValue>
> = [
  {
    value: '<15m',
    label: 'Less than 15 minutes',
    description: 'Near-zero data loss — needs continuous replication or PITR',
    hint: 'Requires streaming replication or transaction-log shipping',
  },
  {
    value: '15m-1h',
    label: '1 hour',
    description: 'Strict — typical for financial or transactional systems',
  },
  {
    value: '1-4h',
    label: '4 hours',
    description: 'Snapshot-based with frequent intervals',
  },
  {
    value: '4-24h',
    label: '24 hours',
    description: 'Default for early-stage SaaS — matches daily backup cadence',
    hint: 'Aligns with most managed-DB default backup retention',
  },
  {
    value: '>24h',
    label: 'More than 24 hours',
    description: 'Backups taken less frequently than daily',
  },
] as const;

export const ONBOARDING_MATURITY_OPTIONS: ReadonlyArray<
  OnboardingOption<OnboardingMaturityValue>
> = [
  {
    value: 'NOT_STARTED',
    label: 'Not started',
    description: 'No policies or controls in place yet — starting fresh',
  },
  {
    value: 'IN_PROGRESS',
    label: 'In progress',
    description: 'Some policies or controls exist, but the program has gaps',
  },
  {
    value: 'NEAR_READY',
    label: 'Near ready',
    description: 'Most controls are in place — close to an audit',
  },
] as const;

export const ONBOARDING_PREVIOUS_PLATFORM_OPTIONS: ReadonlyArray<
  OnboardingOption<OnboardingPreviousPlatformValue>
> = [
  {
    value: 'none',
    label: 'None',
    description: 'Not using a compliance platform today',
  },
  { value: 'vanta', label: 'Vanta', description: 'Switching from Vanta' },
  { value: 'drata', label: 'Drata', description: 'Switching from Drata' },
  {
    value: 'secureframe',
    label: 'Secureframe',
    description: 'Switching from Secureframe',
  },
  {
    value: 'thoropass',
    label: 'Thoropass',
    description: 'Switching from Thoropass',
  },
  { value: 'sprinto', label: 'Sprinto', description: 'Switching from Sprinto' },
  {
    value: 'other',
    label: 'Other',
    description: 'A different compliance platform or a vCISO',
  },
] as const;

export const ONBOARDING_HIPAA_ENTITY_TYPE_OPTIONS: ReadonlyArray<
  OnboardingOption<OnboardingHipaaEntityTypeValue>
> = [
  {
    value: 'BUSINESS_ASSOCIATE',
    label: 'Business Associate',
    description:
      'You handle PHI on behalf of healthcare customers under contract. Most healthtech B2B SaaS is here.',
  },
  {
    value: 'COVERED_ENTITY',
    label: 'Covered Entity',
    description:
      'You provide healthcare directly or bill insurance for it — a clinic, hospital, health plan, or provider.',
  },
  {
    value: 'HYBRID',
    label: 'Hybrid',
    description:
      'You do both — e.g. a provider that also sells software to other providers.',
  },
] as const;

export const ONBOARDING_DATA_TYPE_OPTIONS: ReadonlyArray<
  OnboardingOption<OnboardingDataTypeValue>
> = [
  {
    value: 'CUSTOMER_PII',
    label: 'Customer PII',
    description: 'Names, emails, addresses of end-users',
  },
  {
    value: 'PAYMENT',
    label: 'Payment data',
    description: 'Card numbers, billing info, transactions',
  },
  {
    value: 'HEALTH',
    label: 'Health data',
    description: 'PHI, medical records, clinical data',
  },
  {
    value: 'EMPLOYEE',
    label: 'Employee data',
    description: 'HR records, payroll, internal PII',
  },
  {
    value: 'IP',
    label: 'Company or unspecified IP (legacy)',
    description: 'Previous broad declaration; does not establish that your product handles customer IP',
  },
  {
    value: 'CUSTOMER_IP',
    label: 'Customer / third-party intellectual property',
    description: 'Customer or third-party source code, trade secrets, or proprietary models handled by your product; excludes only holding your own source code',
  },
] as const;

export const ONBOARDING_AUDIT_TYPE_OPTIONS: ReadonlyArray<
  OnboardingOption<OnboardingAuditTypeValue>
> = [
  {
    value: 'TYPE_I',
    label: 'Type I',
    description: 'Point-in-time snapshot — proves controls are in place today.',
  },
  {
    value: 'TYPE_II',
    label: 'Type II',
    // Window matches the wizard explanation and the agent-facing question:
    // 3–12 months, not the 6–12 this once said.
    description: 'Observation period (3–12 months) — stronger proof over time.',
  },
] as const;

export const ONBOARDING_PROGRAM_DEPTH_OPTIONS: ReadonlyArray<
  OnboardingOption<OnboardingProgramDepthValue>
> = [
  {
    value: 'MINIMUM',
    label: 'Baseline',
    description:
      'A risk-based starting scope for a first audit in any framework. Activates the core control set auditors consistently require and defers the rest — visibly, with a reason, one click to turn on.',
  },
  {
    value: 'BALANCED',
    label: 'Enterprise-ready',
    description:
      'The complete control set. Best for renewals, larger teams, and enterprise customers reviewing your report.',
  },
] as const;

export const ONBOARDING_INFRASTRUCTURE_MODEL_OPTIONS: ReadonlyArray<
  OnboardingOption<OnboardingInfrastructureModelValue>
> = [
  {
    value: 'cloud',
    label: 'Fully cloud-hosted',
    description: 'No physical servers — AWS, GCP, Azure, Vercel, etc.',
  },
  {
    value: 'on-prem',
    label: 'On-premises',
    description: 'Physical servers in your own data center or co-lo',
  },
  {
    value: 'hybrid',
    label: 'Hybrid',
    description: 'Mix of cloud and on-premises infrastructure',
  },
  {
    value: 'govcloud',
    label: 'Government cloud',
    description: 'FedRAMP / GovCloud regions (AWS GovCloud, Azure Government)',
  },
] as const;

const normalizeToken = (value: string) =>
  value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '');

function buildAliasMap<TValue extends string>(
  options: ReadonlyArray<OnboardingOption<TValue>>,
  aliases: Record<string, TValue> = {},
): Map<string, TValue> {
  const map = new Map<string, TValue>();
  for (const option of options) {
    map.set(normalizeToken(option.value), option.value);
    map.set(normalizeToken(option.label), option.value);
  }
  for (const [alias, value] of Object.entries(aliases)) {
    map.set(normalizeToken(alias), value);
  }
  return map;
}

const TEAM_SIZE_ALIAS_MAP = buildAliasMap(ONBOARDING_TEAM_SIZE_OPTIONS, {
  'just me': '1',
  solo: '1',
  'solo founder': '1',
  '2–10': '2-10',
  '11–50': '11-50',
  '51–200': '51-200',
  '1-10': '2-10',
});

const INDUSTRY_ALIAS_MAP = buildAliasMap(ONBOARDING_INDUSTRY_OPTIONS, {
  b2bsaas: 'B2B_SAAS',
});

const WORK_MODEL_ALIAS_MAP = buildAliasMap(ONBOARDING_WORK_MODEL_OPTIONS, {
  'in office': 'OFFICE',
  inoffice: 'OFFICE',
});

const RTO_ALIAS_MAP = buildAliasMap(ONBOARDING_RTO_OPTIONS, {
  // Common phrasings agents and humans use.
  'lessthan1hour': '<1h',
  'under1hour': '<1h',
  'sub1hour': '<1h',
  '<1hour': '<1h',
  '1to4hours': '1-4h',
  '14hours': '1-4h',
  '1hour': '1-4h',
  '4to24hours': '4-24h',
  '424hours': '4-24h',
  oneday: '4-24h',
  '24hours': '4-24h',
  '1to3days': '1-3d',
  '13days': '1-3d',
  fewdays: '1-3d',
  'morethan3days': '>3d',
  '3days': '>3d',
  weekormore: '>3d',
});

const RPO_ALIAS_MAP = buildAliasMap(ONBOARDING_RPO_OPTIONS, {
  'lessthan15minutes': '<15m',
  'under15min': '<15m',
  '15min': '<15m',
  '<15min': '<15m',
  '15minutesto1hour': '15m-1h',
  '15min1h': '15m-1h',
  '1to4hours': '1-4h',
  '14h': '1-4h',
  '4to24hours': '4-24h',
  '424h': '4-24h',
  daily: '4-24h',
  '24h': '4-24h',
  'morethan24hours': '>24h',
  '24hour': '>24h',
});

const MATURITY_ALIAS_MAP = buildAliasMap(ONBOARDING_MATURITY_OPTIONS, {
  notstarted: 'NOT_STARTED',
  inprogress: 'IN_PROGRESS',
  nearready: 'NEAR_READY',
  almostready: 'NEAR_READY',
});

// No extra aliases needed — every value is already the lowercased label.
const PREVIOUS_PLATFORM_ALIAS_MAP = buildAliasMap(
  ONBOARDING_PREVIOUS_PLATFORM_OPTIONS,
);

// `buildAliasMap` already derives value + label tokens; only the
// CE/BA shorthand needs to be added explicitly.
const HIPAA_ENTITY_TYPE_ALIAS_MAP = buildAliasMap(
  ONBOARDING_HIPAA_ENTITY_TYPE_OPTIONS,
  {
    ce: 'COVERED_ENTITY',
    ba: 'BUSINESS_ASSOCIATE',
  },
);

function normalizeFromMap<TValue extends string>(
  value: unknown,
  aliasMap: Map<string, TValue>,
): TValue | undefined {
  if (typeof value !== 'string') return undefined;
  return aliasMap.get(normalizeToken(value));
}

export function normalizeOnboardingTeamSize(
  value: unknown,
): OnboardingTeamSizeValue | undefined {
  return normalizeFromMap(value, TEAM_SIZE_ALIAS_MAP);
}

export function normalizeOnboardingIndustry(
  value: unknown,
): OnboardingIndustryValue | undefined {
  return normalizeFromMap(value, INDUSTRY_ALIAS_MAP);
}

export function normalizeOnboardingWorkModel(
  value: unknown,
): OnboardingWorkModelValue | undefined {
  return normalizeFromMap(value, WORK_MODEL_ALIAS_MAP);
}

export function normalizeOnboardingRto(
  value: unknown,
): OnboardingRtoValue | undefined {
  return normalizeFromMap(value, RTO_ALIAS_MAP);
}

export function normalizeOnboardingRpo(
  value: unknown,
): OnboardingRpoValue | undefined {
  return normalizeFromMap(value, RPO_ALIAS_MAP);
}

export function normalizeOnboardingMaturity(
  value: unknown,
): OnboardingMaturityValue | undefined {
  return normalizeFromMap(value, MATURITY_ALIAS_MAP);
}

export function normalizeOnboardingPreviousPlatform(
  value: unknown,
): OnboardingPreviousPlatformValue | undefined {
  return normalizeFromMap(value, PREVIOUS_PLATFORM_ALIAS_MAP);
}

export function normalizeOnboardingHipaaEntityType(
  value: unknown,
): OnboardingHipaaEntityTypeValue | undefined {
  return normalizeFromMap(value, HIPAA_ENTITY_TYPE_ALIAS_MAP);
}

// Strict vocabulary: accept only a canonical value or its display label
// (case- and separator-insensitive, both derived automatically by
// `buildAliasMap`). No hand-written synonyms — an agent is shown the canonical
// options and is expected to echo one. Anything else is treated as invalid:
// the field router drops it and the apply preview reports it rejected, so the
// user re-selects in the browser rather than the system storing an
// off-vocabulary guess.
const DATA_TYPE_ALIAS_MAP = buildAliasMap(ONBOARDING_DATA_TYPE_OPTIONS);
const AUDIT_TYPE_ALIAS_MAP = buildAliasMap(ONBOARDING_AUDIT_TYPE_OPTIONS);

export function normalizeOnboardingDataType(
  value: unknown,
): OnboardingDataTypeValue | undefined {
  return normalizeFromMap(value, DATA_TYPE_ALIAS_MAP);
}

export function normalizeOnboardingAuditType(
  value: unknown,
): OnboardingAuditTypeValue | undefined {
  return normalizeFromMap(value, AUDIT_TYPE_ALIAS_MAP);
}

const PROGRAM_DEPTH_ALIAS_MAP = buildAliasMap(ONBOARDING_PROGRAM_DEPTH_OPTIONS);

export function normalizeOnboardingProgramDepth(
  value: unknown,
): OnboardingProgramDepthValue | undefined {
  return normalizeFromMap(value, PROGRAM_DEPTH_ALIAS_MAP);
}

const INFRASTRUCTURE_MODEL_ALIAS_MAP = buildAliasMap(
  ONBOARDING_INFRASTRUCTURE_MODEL_OPTIONS,
);

export function normalizeOnboardingInfrastructureModel(
  value: unknown,
): OnboardingInfrastructureModelValue | undefined {
  return normalizeFromMap(value, INFRASTRUCTURE_MODEL_ALIAS_MAP);
}

export function getOnboardingTeamSizeLabel(
  value: unknown,
): string | undefined {
  const normalized = normalizeOnboardingTeamSize(value);
  return ONBOARDING_TEAM_SIZE_OPTIONS.find(
    (option) => option.value === normalized,
  )?.label;
}

export function getOnboardingIndustryLabel(value: unknown): string | undefined {
  const normalized = normalizeOnboardingIndustry(value);
  return ONBOARDING_INDUSTRY_OPTIONS.find(
    (option) => option.value === normalized,
  )?.label;
}

export function getOnboardingWorkModelLabel(
  value: unknown,
): string | undefined {
  const normalized = normalizeOnboardingWorkModel(value);
  return ONBOARDING_WORK_MODEL_OPTIONS.find(
    (option) => option.value === normalized,
  )?.label;
}
